import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader } from "@/components/common/DashboardBits";
import ExportVisitsButton from "@/components/common/ExportVisitsButton";
import AddLeadDialog from "@/components/leads/AddLeadDialog";
import LeadDetailDialog from "@/components/leads/LeadDetailDialog";
import LeadsTable from "@/components/leads/LeadsTable";
import ShareLeadDialog from "@/components/leads/ShareLeadDialog";
import BankPendingCard from "@/components/bank/BankPendingCard";
import { useBankStatus } from "@/hooks/useBankStatus";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Filter } from "lucide-react";

const STATUS_FILTERS = [["all", "All statuses"], ["pending", "Pending"], ["assigned", "Assigned"], ["in_progress", "In Progress"], ["visited", "Visited"], ["submitted", "Submitted"]];

function useLeadsData(role) {
  const [data, setData] = useState({ leads: [], banks: [], executives: [] });
  const loadAll = useCallback(async () => {
    try {
      const [l, b, e] = await Promise.all([api.get("/leads"), api.get("/banks").catch(() => ({ data: [] })), api.get("/executives").catch(() => ({ data: [] }))]);
      setData({ leads: l.data, banks: b.data, executives: e.data });
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll, role]);
  return { ...data, loadAll };
}

/* Shared leads screen used by admin/bank/office/field with role-based capabilities. */
export default function LeadsPage({ role }) {
  const { t } = useLang();
  const { leads, banks, executives, loadAll } = useLeadsData(role);
  const [filter, setFilter] = useState("all");
  const [openAdd, setOpenAdd] = useState(false);
  const [detail, setDetail] = useState(null);
  const [shareLead, setShareLead] = useState(null); // freshly created lead with documents → offer sharing
  const bankStatus = useBankStatus();

  const canAdd = ["admin", "bank", "office", "field"].includes(role) && bankStatus.approved;
  const isStaff = ["admin", "office"].includes(role);
  const filtered = filter === "all" ? leads : leads.filter((x) => x.status === filter);

  const onSaved = (created) => {
    setOpenAdd(false);
    loadAll();
    toast.success("Lead created");
    if (created?.documents?.length) setShareLead(created);
  };
  const deleteLead = async (l) => {
    if (!window.confirm("Delete lead?")) return;
    try { await api.delete(`/leads/${l.id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      {role === "bank" && !bankStatus.loading && !bankStatus.approved && <BankPendingCard feature="Lead creation and document sharing" />}
      <PageHeader
        title={role === "field" ? t("my_leads") : t("leads")}
        subtitle={`${leads.length} total`}
        right={
          <div className="flex gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[160px]" data-testid="leads-filter"><Filter className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_FILTERS.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}</SelectContent>
            </Select>
            {isStaff && <ExportVisitsButton />}
            {canAdd && (
              <Dialog open={openAdd} onOpenChange={setOpenAdd}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-950 hover:bg-slate-800" data-testid="add-lead-button"><Plus className="h-4 w-4 mr-1" /> {t("add_lead")}</Button>
                </DialogTrigger>
                <AddLeadDialog role={role} banks={banks} onClose={() => setOpenAdd(false)} onSaved={onSaved} />
              </Dialog>
            )}
          </div>
        }
      />

      <LeadsTable leads={filtered} actionsLabel={t("actions")} onView={setDetail} onDelete={role === "admin" ? deleteLead : undefined} />

      {detail && (
        <LeadDetailDialog lead={detail} onClose={() => setDetail(null)} onChanged={() => { loadAll(); setDetail(null); }}
          executives={executives} banks={banks} role={role} canAssign={isStaff} canSubmit={isStaff} canVisit={role === "field"} />
      )}
      {shareLead && <ShareLeadDialog lead={shareLead} open justCreated onClose={() => setShareLead(null)} />}
    </div>
  );
}
